import { getSignedUrlForFile } from "../../custom-hooks/useSignedUrlForFile.jsx";
import { fetchUnitDocumentsByUnitId } from "./fetchUnitDocumentsByUnitId.jsx";

/**
 * Choose one large image per imageSetId:
 * - Prefer an "uncropped" large if we can detect it (key contains "1024")
 * - Else take the newest by createdAt
 */
export const makeFetchUnitImageSignedUrls = ({
  largeTypeId,
  largeTypeName = "unit-photo-large",
  onlyImages = true,
} = {}) => {
  const isImageMime = (mime) => /^image\//i.test(mime || "");
  const isImageExt = (key) => {
    const ext = (key || "").split(".").pop()?.toLowerCase();
    return new Set([
      "jpg","jpeg","png","gif","webp","bmp","tif","tiff","heic","heif","avif",
    ]).has(ext);
  };

  const looksUncropped = (key) => {
    const k = (key || "").toLowerCase();
    // tweak this heuristic to match your naming if different
    return k.includes("1024") || k.includes("uncropped");
  };

  return async (unit) => {
    if (!unit?.id) return [];

    try {
      const docs = await fetchUnitDocumentsByUnitId(unit.id);
      if (!Array.isArray(docs)) return [];

      // 1) keep only "large" documents (by id or by name)
      let largeDocs = docs.filter((doc) => {
        const up = doc?.uploadedDocument;
        if (!up?.bucket || !up?.key) return false;

        const tId = doc?.unitDocumentTypeId;
        const tName = doc?.unitDocumentType?.name;

        const matchesId = largeTypeId ? tId === largeTypeId : false;
        const matchesName = tName === largeTypeName;
        return matchesId || matchesName;
      });

      // 2) (optional) ensure they’re images (exclude PDFs, etc.)
      if (onlyImages) {
        largeDocs = largeDocs.filter((doc) => {
          const up = doc?.uploadedDocument;
          const mime = up?.contentType || up?.mimeType || doc?.contentType || doc?.mimeType;
          return isImageMime(mime) || isImageExt(up?.key);
        });
      }

      // 3) Group by imageSetId and pick exactly one per group
      const bySet = new Map();
      for (const doc of largeDocs) {
        const setId = doc?.imageSetId || "__no_set__";
        const existing = bySet.get(setId);

        if (!existing) {
          bySet.set(setId, doc);
          continue;
        }

        const keyNew = doc?.uploadedDocument?.key || "";
        const keyOld = existing?.uploadedDocument?.key || "";

        const newIsUncropped = looksUncropped(keyNew);
        const oldIsUncropped = looksUncropped(keyOld);

        if (newIsUncropped && !oldIsUncropped) {
          bySet.set(setId, doc);
          continue;
        }
        if (!newIsUncropped && oldIsUncropped) {
          continue; // keep existing uncropped
        }

        // If both (or neither) look uncropped, choose newest by createdAt
        const newer =
          (doc?.createdAt || "") > (existing?.createdAt || "") ? doc : existing;
        bySet.set(setId, newer);
      }

      const chosen = Array.from(bySet.values());

      // Sort newest first (optional)
      chosen.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

      // 4) Sign the chosen docs
      const urls = await Promise.all(
        chosen.map(async (doc) => {
          const { bucket, key, identityId } = doc.uploadedDocument || {};
          try {
            return await getSignedUrlForFile({
              bucketName: bucket,
              identityId,
              fileName: key,
              fileSecurity: "protected",
            });
          } catch (err) {
            console.error("Signed URL error (unit large image):", err);
            return null;
          }
        })
      );

      return urls.filter(Boolean);
    } catch (err) {
      console.error("makeFetchUnitImageSignedUrls failed:", err);
      return [];
    }
  };
};

export const fetchUnitImageSignedUrls = makeFetchUnitImageSignedUrls();